import React from 'react';

import {
  Close,
  InsertDriveFile,
  Upload
} from '@mui/icons-material';
import {
  Box,
  Divider as BaseDivider,
  IconButton,
  Stack,
  styled,
  Typography,
  Chip
} from '@mui/material';
import Bluebird from 'bluebird';
import _ from 'lodash';
import {
  FileInputField,
  IFileData
} from 'modules/layer2';
import papaparse from 'papaparse';
import * as yup from 'yup';



const BorderBox = styled(Box)(({ theme }) => ({
  background: 'rgba(135, 172, 185, 0.1)',
  border: '1px dashed #87acb9',
  borderRadius: theme.shape.borderRadius
}));

const ContentStack = styled(Stack)(({ theme }) => ({
  alignItems: 'center',
  margin: theme.spacing(3)
}));

const ChooseFileTypography = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontSize: '14px'
}));

const FileSizeTypography = styled(Typography)(({ theme }) => ({
  color: '#486c82',
  fontSize: '10px',
  opacity: 0.75
}));

const InputtedFileBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  padding: theme.spacing(1),
  border: '1px solid rgba(30, 57, 73, 0.16)',
  borderRadius: theme.shape.borderRadius,
  alignItems: 'center'
}));

const Divider = styled(BaseDivider)(({ theme }) => ({
  height: 30
}));



export interface IValidateStatus {
  status: 'notStarted' | 'working' | 'complete' | 'error';
  progressPercent: number;
  message: string;
}



export interface IProps<T extends yup.AnyObjectSchema> {
  disabled?: boolean;
  rowValidator: T;
  key2FnTransformRow?: Partial<Record<keyof yup.InferType<T>, (value: yup.InferType<T>[keyof yup.InferType<T>]) => yup.InferType<T>[keyof yup.InferType<T>]>>;
  onSuccess: (results: Array<yup.InferType<T>>) => void;
  onError: (error: string[]) => void;
  onDelete: () => void;
}

export const FC = <T extends yup.AnyObjectSchema>(
  props: IProps<T> & { children?: React.ReactNode }
) => {
  const [file, setFile] = React.useState<IFileData | null>(null);
  const [parseErrors, setParseErrors] = React.useState<string[]>([]);
  const [status, setStatus] = React.useState<IValidateStatus>({
    status: 'notStarted',
    progressPercent: 0,
    message: ''
  });

  function handleClickDelete() {
    setFile(null);
    setParseErrors([]);
    props.onDelete();
  }



  function handleValidateStatusUpdate(status: IValidateStatus) {
    setStatus(status);
  }


  return (
    <Stack spacing={3}>
      <FileInputField
        disabled={props.disabled}
        readAsText
        acceptTypes={['text/csv']}
        onSelectFile={file => {
          if (file.fileSizeBytes > 5 * 1024 * 1024) {
            alert('File must be less than 5MB');
          } else {
            setFile(file);

            if (handleValidateStatusUpdate) {
              handleValidateStatusUpdate({
                status: 'working',
                progressPercent: 0,
                message: 'Parsing file...'
              });
            }

            papaparse.parse<T>(file.dataUrl, {
              header: true,
              delimiter: ',',
              skipEmptyLines: true,
              transform: (value => value === '' ? undefined : value), // Regard unfilled column as `undefined`
              complete: async results => {
                if (handleValidateStatusUpdate) {
                  handleValidateStatusUpdate({
                    status: 'working',
                    progressPercent: 0,
                    message: 'Validating...'
                  });
                }

                const errs = await Bluebird.mapSeries(results.data, async (row, i) => {
                  try {
                    const remappedValue = _.mapValues(row, (value, key) => {
                      if (props.key2FnTransformRow?.[key]) {
                        return props.key2FnTransformRow?.[key]?.(value);
                      }
                      return value;
                    });

                    await props.rowValidator.validate(remappedValue);
                    handleValidateStatusUpdate({
                      status: 'working',
                      progressPercent: Math.floor(i / results.data.length * 100),
                      message: 'Validating rows...'
                    });

                  } catch (e: unknown) {
                    if (e instanceof Error) {
                      return `Row ${i}: ${e.message}`;
                    }

                    return `Row ${i}: Data validation error`;
                  }

                  return '';
                });

                const allErrors = _.compact([
                  ..._.map(results.errors, err => `Row ${err.row}: ${err.message}`),
                  ...errs
                ]);
                setParseErrors(allErrors);

                if (_.isEmpty(allErrors)) {
                  handleValidateStatusUpdate({
                    message: 'No validation errors',
                    status: 'complete',
                    progressPercent: 100
                  });
                  props.onSuccess(_.map(results.data, d => _.mapValues(d, (value, key) => props.key2FnTransformRow?.[key]?.(value) ?? value)));

                } else {
                  handleValidateStatusUpdate({
                    message: `${allErrors.length} validation errors`,
                    status: 'error',
                    progressPercent: 100
                  });
                  props.onError(allErrors);
                }
              }
            });

          }
        }}>
        {handleClick => (
          <BorderBox
            style={{ cursor: props.disabled ? 'default' : 'pointer' }}
            onClick={handleClick}>
            <ContentStack>
              <Box>
                <Upload style={{ color: '#87ACB9' }} />
              </Box>

              <ChooseFileTypography>Choose File</ChooseFileTypography>

              <FileSizeTypography variant="subtitle2">Maximum size: 5MB</FileSizeTypography>
            </ContentStack>
          </BorderBox>
        )}
      </FileInputField>

      {file && (
        <InputtedFileBox>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center">
            <InsertDriveFile />

            <Typography>{file.name}</Typography>
          </Stack>

          <Stack
            direction="row"
            spacing={1}
            alignItems="center">
            <Chip label={__genFileSizeLabelFromBytes(file.fileSizeBytes)} />

            <Divider orientation="vertical" />

            <IconButton
              disabled={props.disabled}
              size="small"
              onClick={handleClickDelete}>
              <Close style={{ color: '#87ACB9' }} />
            </IconButton>
          </Stack>
        </InputtedFileBox>
      )}

      <Stack>
        <Typography>{status.message}</Typography>

        {_.map(parseErrors, (err, i) => (
          <Typography
            key={i}
            color="error"
            variant="subtitle2">
            {err}
          </Typography>
        ))}
      </Stack>
    </Stack>
  );
};



// Converts a number in bytes to an appropriate abbreviated value appending the units.
function __genFileSizeLabelFromBytes(bytes: number) {
  const units = ['KB', 'MB', 'GB'];

  const fac = _.reduce(units, (last, unit) => {
    const newValue = last.value / 1024;
    if (newValue < 1) {
      return last;
    }

    return {
      value: newValue,
      unit
    };
  }, {
    value: bytes,
    unit: 'B'
  });

  return _.ceil(fac.value) + fac.unit;
}

